/**
 * Every colour Daedalus draws on a canvas (ADR-070 decision 2), in one table of plain data: the renderer's
 * overlays and markers and `daedalus-ui`'s cell editors and samples read their colours here, since neither
 * may import the other and a canvas cannot read a CSS custom property per pixel. Each entry names the token
 * of `daedalus-ui/src/theme/tokens.css` it stands for and holds that token's colour and alpha, which a
 * repository test compares with the stylesheet. A draw site scales an entry's alpha by an opacity of its own
 * (a fill drawn at 35%, a ghost at 14%); the colour itself changes only here and in the token.
 */

/**
 * One canvas colour.
 */
export interface CanvasColour {
  /** The token in `tokens.css` this colour is, without its `--dd-` prefix. */
  readonly token: string;
  /** The colour as `0xrrggbb`. */
  readonly rgb: number;
  /** The token's own alpha, from 0 to 1: 1 for an opaque token. */
  readonly alpha: number;
}

const GOLD: CanvasColour = { token: "gold", rgb: 0xe8b54d, alpha: 1 };
const INK: CanvasColour = { token: "ink", rgb: 0xece7df, alpha: 1 };
const SURFACE_0: CanvasColour = { token: "surface-0", rgb: 0x0f0e0d, alpha: 1 };
const OVERLAY_GRID: CanvasColour = { token: "overlay-grid", rgb: 0xece7df, alpha: 0x1f / 255 };

/**
 * The canvas colours by use.
 */
export const CANVAS_COLOURS = {
  /** The map's tile grid. */
  grid: OVERLAY_GRID,
  /** The cursor ring and the brush's highlight on the map. */
  cursor: GOLD,
  /** The tint of a selected prop, its box's edge and its corner handles on the map. */
  selectedProp: GOLD,
  /** The keyline beside a selected prop's box and round its handles, so the gold holds on light ground. */
  selectedPropOutline: SURFACE_0,
  /** The dark wash of a light's night preview in the studio's Light tab, and the anchor's crosshair over it. */
  lightPreviewNight: SURFACE_0,
  lightAnchor: GOLD,
  /** The placement ghost's marks. */
  placementGhost: GOLD,
  /** A selected entity's marker and extent. */
  selectedEntity: GOLD,
  /** The tile selection's rectangle and the outline of where a moved selection lands. */
  tileSelection: { token: "overlay-selection-tile", rgb: 0xe8b54d, alpha: 1 },
  /** The keyline outside the tile selection's edge and the moved selection's outline, so gold holds on light ground. */
  tileSelectionOutline: SURFACE_0,
  /** The marquee a Select drag on empty ground draws (P1.6-T33), gold as every selection is. */
  marquee: GOLD,
  /** The keyline outside the marquee's edge. */
  marqueeOutline: SURFACE_0,
  /** An unselected entity's marker and extent. */
  entity: { token: "kind-entities", rgb: 0x62c6ee, alpha: 1 },
  /** The ring around an entity marker. */
  entityOutline: SURFACE_0,
  /** An entity marker's label. */
  entityLabel: INK,
  /** The plate behind an entity marker's label. */
  entityLabelBackground: { token: "scrim", rgb: 0x0f0e0d, alpha: 0x99 / 255 },
  /** The hatch across a collision cell painted on the collision layer. */
  collisionHatch: INK,
  /** A collision flag the game registers no tint for. */
  collisionUntinted: { token: "kind-collision", rgb: 0xee6f64, alpha: 1 },
  /** A cell the game's opening paint opens and nothing blocks: violet, apart from green, blue, sand and grey ground and from the red, blue and amber of what blocks. */
  collisionOpen: { token: "kind-zones", rgb: 0xae99f6, alpha: 1 },
  /** A blocked cell in the footprint editors. */
  footprintCell: { token: "overlay-footprint", rgb: 0xee6f64, alpha: 1 },
  /** The cell lines of the footprint and activation editors. */
  footprintLines: OVERLAY_GRID,
  /** A cell a cell editor shows behind the painted ones, such as the default activation ring. */
  cellGhost: INK,
  /** A painted cell in the activation editors. */
  activationCell: { token: "overlay-activation", rgb: 0x6a9fee, alpha: 1 },
  /** The ground behind a terrain sample. */
  sampleBackground: SURFACE_0,
  /** The player wash: the green over whatever a character standing on a row would be drawn under. */
  playerWash: { token: "overlay-player-wash", rgb: 0x3dff6e, alpha: 1 },
  /** The player stand-in's box, drawn while the game's art is left out or has not loaded. */
  standIn: { token: "overlay-stand-in", rgb: 0x39ff6a, alpha: 1 },
  /** The stand-in box's 2 px edge, near-black so it holds on every ground and over the wash; the keyline round the footprint panel's feet box too. */
  standInEdge: { token: "overlay-stand-in-edge", rgb: 0x0f0e0d, alpha: 1 },
  /** The line across the stand-in's feet and the outline of its feet box. */
  standInFeet: { token: "overlay-stand-in-feet", rgb: 0x06381a, alpha: 1 },
  /** The footprint panel's feet box where a character could stand: the stand-in's green. */
  feetFree: { token: "overlay-stand-in", rgb: 0x39ff6a, alpha: 1 },
  /** The feet box where the footprint stops a character: the footprint's own colour, under a cross. */
  feetBlocked: { token: "overlay-footprint", rgb: 0xee6f64, alpha: 1 },
  /** The cross over the feet box where it is stopped. */
  feetMark: INK,
  /** The camera-stop guide: the band's tint along each map edge and its dashed inner edge. */
  cameraStop: { token: "overlay-camera-stop", rgb: 0xdcebff, alpha: 1 },
  /** The outline marking a template piece that carries a behaviour, in the template's preview. */
  templateBehaviour: GOLD,
  /** The cross on the selected behaviour prop's feet point on the map. */
  activationFeet: GOLD,
  /** Activation overlay category 1, blue, solid (ADR-070's amendment of 2026-09-27). */
  activationCategory1: { token: "overlay-category-1", rgb: 0x6a9fee, alpha: 1 },
  /** Activation overlay category 2, orange, diagonal hatch. */
  activationCategory2: { token: "overlay-category-2", rgb: 0xe58212, alpha: 1 },
  /** Activation overlay category 3, aqua, dots. */
  activationCategory3: { token: "overlay-category-3", rgb: 0x35dde2, alpha: 1 },
  /** Activation overlay category 4, raspberry, horizontal lines. */
  activationCategory4: { token: "overlay-category-4", rgb: 0xc4486c, alpha: 1 },
  /** Activation overlay category 5, pale lime, cross-hatch. */
  activationCategory5: { token: "overlay-category-5", rgb: 0xbdfda7, alpha: 1 },
  /** Activation overlay category 6, indigo, back-diagonal hatch. */
  activationCategory6: { token: "overlay-category-6", rgb: 0x636dd7, alpha: 1 },
  /** Activation overlay category 7, mauve, vertical lines. */
  activationCategory7: { token: "overlay-category-7", rgb: 0xba929d, alpha: 1 },
  /** Activation overlay category 8, sage, checker. */
  activationCategory8: { token: "overlay-category-8", rgb: 0xb6cf8f, alpha: 1 },
  /** The keyline outside each area's edge in the Activation overlay and the ring of its number tag. */
  activationKeyline: SURFACE_0,
  /** The outline around the cells of a behaviour prop the compile would refuse, in the Activation overlay. */
  activationIssueError: { token: "error", rgb: 0xff9ab8, alpha: 1 },
  /** The outline around the cells of a behaviour prop with a warning alone, in the Activation overlay. */
  activationIssueWarning: { token: "warning", rgb: 0xf09a45, alpha: 1 },
  /** A reference link on the map, from the referring thing to its target, with its arrowhead (P1.6-T35). */
  link: { token: "overlay-link", rgb: 0xece7df, alpha: 1 },
  /** The areas a selected thing's cells fields hold on the map (P1.6-T36), in the zone kind's colour. */
  fieldCells: { token: "kind-zones", rgb: 0xae99f6, alpha: 1 },
  /** The parts a game carves a selected thing's cells field into and draws over it, in the success colour. */
  fieldCellsPart: { token: "success", rgb: 0x78c98e, alpha: 1 },
  /** The paths a selected thing's path fields hold on the map (ADR-136), gold as the thing's own marks are. */
  fieldPath: GOLD,
  /** Highlight layer: the cells and things of the active layer that show (ADR-070's amendment of 2026-10-04), raspberry. */
  layerHighlight: { token: "overlay-category-4", rgb: 0xc4486c, alpha: 1 },
  /** Highlight layer: the cells covered by a tile or prop above, indigo, striped and quieter. */
  layerHighlightCovered: { token: "overlay-category-6", rgb: 0x636dd7, alpha: 1 },
  /** Highlight layer: the cells only estimated to be under a tile or prop, indigo, the faintest. */
  layerHighlightUnder: { token: "overlay-category-6", rgb: 0x636dd7, alpha: 1 },
  /** The keyline outside a highlight's edge, so it holds on any ground. */
  layerHighlightKeyline: SURFACE_0,
  /** The density meter's measured range while its worst graded reading is green, or none is graded (ADR-088). */
  densityGreen: { token: "success", rgb: 0x78c98e, alpha: 1 },
  /** The measured range while its worst reading is amber. */
  densityAmber: { token: "warning", rgb: 0xf09a45, alpha: 1 },
  /** The measured range while a reading is red. */
  densityRed: { token: "error", rgb: 0xff9ab8, alpha: 1 },
  /** The camera view the range is measured around, dashed inside it. */
  densityView: INK,
  /** The rectangle a group reaching around the view is counted over, such as the server's interest, dashed. */
  densityReach: { token: "kind-entities", rgb: 0x62c6ee, alpha: 1 },
  /** The prop brush's ring while it erases, and the marks over the props it takes (ADR-123 decision 5.1). */
  brushErase: { token: "error", rgb: 0xff9ab8, alpha: 1 },
  /** The box where the editor's atlas has no picture for a placed prop or tile (ADR-129 decision 3.6). */
  missingFrame: { token: "warning", rgb: 0xf09a45, alpha: 1 },
  /** The keyline under that box's edge. */
  missingFrameOutline: SURFACE_0,
} as const satisfies Readonly<Record<string, CanvasColour>>;

/**
 * A canvas colour as a CSS `rgba()` string, for a 2D context's fill and stroke styles or an element's
 * background.
 *
 * @param colour The colour.
 * @param opacity How much of the colour's own alpha to draw, from 0 to 1.
 * @returns `rgba(r, g, b, a)`, the alpha rounded to three places.
 * @throws RangeError when the opacity is outside 0 to 1.
 * @example cssColour(CANVAS_COLOURS.activationCell, 0.35) returns "rgba(106, 159, 238, 0.35)".
 */
export function cssColour(colour: CanvasColour, opacity = 1): string {
  if (!(opacity >= 0 && opacity <= 1)) {
    throw new RangeError(`opacity ${opacity} is outside [0, 1]`);
  }
  const red = (colour.rgb >>> 16) & 0xff;
  const green = (colour.rgb >>> 8) & 0xff;
  const blue = colour.rgb & 0xff;
  const alpha = Math.round(colour.alpha * opacity * 1000) / 1000;
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

/**
 * A colour read from art (a sprite's mean colour, drawn as one pixel by the map picture's painter), as an opaque CSS
 * colour. It is no token and is in no table: it is the one way a colour that is data, not chrome, is written.
 *
 * @param colour The channels, each a whole number from 0 to 255.
 * @returns `rgba(r, g, b, 1)`.
 * @throws RangeError when a channel is not a whole number from 0 to 255.
 */
export function artColour(colour: { readonly r: number; readonly g: number; readonly b: number }): string {
  for (const channel of [colour.r, colour.g, colour.b]) {
    if (!Number.isInteger(channel) || channel < 0 || channel > 255) {
      throw new RangeError(`a colour channel is a whole number from 0 to 255, got ${channel}`);
    }
  }
  return `rgba(${colour.r}, ${colour.g}, ${colour.b}, 1)`;
}
